import React from 'react';
import Link from 'next/link';
import {
  Zap,
  CheckCircle2,
  Sparkles,
} from 'lucide-react';
import TTOSRobotMascot from './TTOSRobotMascot';

export const TTOSHeroV2: React.FC = () => {
  return (
    <section className="relative pt-32 pb-20 md:pt-36 md:pb-24 overflow-hidden bg-gradient-to-b from-[#F5F3FF]/70 via-white to-white">
      {/* Background Glows */}
      <div className="absolute top-10 left-1/2 -translate-x-1/2 w-[900px] h-[450px] bg-gradient-to-tr from-purple-300/30 via-indigo-200/30 to-sky-200/30 blur-[120px] -z-10 rounded-full pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          
          {/* Left Column */}
          <div className="lg:col-span-6 space-y-6 text-left">
            
            {/* Top Pill Badge */}
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-purple-100/70 border border-purple-200 text-purple-700 text-xs font-semibold shadow-xs">
              <Zap className="w-3.5 h-3.5 text-purple-600 fill-purple-600" />
              <span>Powerful Features</span>
            </div>

            {/* Main Headline */}
            <h1 className="text-4xl sm:text-5xl lg:text-[52px] font-extrabold text-slate-900 tracking-tight leading-[1.14]">
              Everything You Need to{' '}
              <br />
              <span className="bg-gradient-to-r from-purple-600 via-indigo-600 to-violet-600 bg-clip-text text-transparent">
                Run Your Business with AI
              </span>
            </h1>

            {/* Subtitle */}
            <p className="text-base sm:text-lg text-slate-600 font-normal leading-relaxed max-w-xl">
              TT OS brings together 30+ powerful tools, AI agents and automation to help you build, market, grow and scale — all in one place.
            </p>

            {/* 4 Key Guarantee Checkmark Pills */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
              <div className="flex items-start gap-2.5">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                <div>
                  <h4 className="text-xs font-bold text-slate-900">Industry-Specific</h4>
                  <p className="text-[11px] text-slate-500">Built for your business type</p>
                </div>
              </div>

              <div className="flex items-start gap-2.5">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                <div>
                  <h4 className="text-xs font-bold text-slate-900">AI Powered</h4>
                  <p className="text-[11px] text-slate-500">Works automatically</p>
                </div>
              </div>

              <div className="flex items-start gap-2.5">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                <div>
                  <h4 className="text-xs font-bold text-slate-900">Save Time</h4>
                  <p className="text-[11px] text-slate-500">Do more, faster</p>
                </div>
              </div>

              <div className="flex items-start gap-2.5">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                <div>
                  <h4 className="text-xs font-bold text-slate-900">Better Results</h4>
                  <p className="text-[11px] text-slate-500">Grow with confidence</p>
                </div>
              </div>
            </div>

            {/* CTAs */}
            <div className="flex flex-wrap items-center gap-3.5 pt-2">
              <Link
                href="/register"
                className="px-7 py-3 rounded-full bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-700 hover:from-indigo-700 hover:to-purple-700 text-white text-sm font-bold shadow-lg shadow-purple-500/25 transition-all hover:scale-[1.02]"
              >
                Start Free Trial
              </Link>
              <Link
                href="#features-grid"
                className="px-6 py-3 rounded-full bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 text-sm font-semibold transition-all shadow-xs"
              >
                Browse All 30+ Features ↓
              </Link>
            </div>

          </div>

          {/* Right Column: Hero Dashboard Command Center Mockup */}
          <div className="lg:col-span-6 relative">
            <div className="relative rounded-2xl bg-white border border-slate-200/90 shadow-2xl p-5 md:p-6 space-y-4">
              
              {/* Top Bar with Search & Quick Status */}
              <div className="flex items-center justify-between pb-3 border-b border-slate-100 text-xs">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-400" />
                  <div className="w-3 h-3 rounded-full bg-amber-400" />
                  <div className="w-3 h-3 rounded-full bg-emerald-400" />
                  <span className="text-slate-400 font-mono text-[11px] ml-2">ttos-hub.internal/workspace</span>
                </div>
                <div className="flex items-center gap-2 text-emerald-600 font-semibold text-[11px]">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 animate-ping" />
                  Autonomous AI Active
                </div>
              </div>

              {/* Central Business Owner Welcome with Mascot */}
              <div className="flex items-center justify-between p-4 rounded-xl bg-gradient-to-r from-indigo-50/80 via-purple-50/80 to-sky-50/80 border border-purple-200/60">
                <div className="space-y-1">
                  <span className="px-2 py-0.5 rounded-md bg-purple-600 text-white text-[10px] font-extrabold uppercase">
                    Operating Hub
                  </span>
                  <h3 className="text-sm font-bold text-slate-900">Good morning, Business Owner! 👋</h3>
                  <p className="text-[11px] text-slate-600">32 autonomous marketing tasks executed overnight.</p>
                </div>
                <TTOSRobotMascot size={80} glow={false} />
              </div>

              {/* 3 Live Mini Cards */}
              <div className="grid grid-cols-3 gap-2 text-center text-xs">
                <div className="p-2.5 rounded-xl bg-slate-50 border border-slate-100">
                  <span className="text-[10px] text-slate-500 block">Website Health</span>
                  <span className="text-sm font-black text-emerald-600">98%</span>
                </div>
                <div className="p-2.5 rounded-xl bg-slate-50 border border-slate-100">
                  <span className="text-[10px] text-slate-500 block">Social Reach</span>
                  <span className="text-sm font-black text-indigo-600">18.4K</span>
                </div>
                <div className="p-2.5 rounded-xl bg-slate-50 border border-slate-100">
                  <span className="text-[10px] text-slate-500 block">New Leads</span>
                  <span className="text-sm font-black text-purple-600">142</span>
                </div>
              </div>

              {/* AI Auto-Trigger Pipeline Preview */}
              <div className="p-3 bg-slate-900 text-white rounded-xl text-xs space-y-2">
                <div className="flex items-center justify-between text-[10.5px] text-slate-400">
                  <span className="flex items-center gap-1.5 text-emerald-400 font-semibold">
                    <Sparkles className="w-3.5 h-3.5" />
                    Real-Time Pipeline Execution
                  </span>
                  <span>100% Automated</span>
                </div>
                <div className="space-y-1 text-[11px] text-slate-300 font-mono">
                  <p className="truncate text-emerald-400">✓ Inbound WhatsApp Lead → Auto Qualified & CRM Synced</p>
                  <p className="truncate text-sky-400">✓ SEO Blog &quot;Best Dental Care Tips&quot; → Auto Published</p>
                  <p className="truncate text-purple-400">✓ 5-Slide Instagram Carousel → Scheduled for 6:30 PM</p>
                </div>
              </div>

            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default TTOSHeroV2;
